import { useMediaQuery } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { EventData } from 'react-joyride';
import { ACTIONS, Joyride, STATUS } from 'react-joyride';
import { useDispatch, useSelector } from 'react-redux';

import { mediumScreen } from '@/src/shared/constants/MediaQueryConstants';
import { useAppRoute } from '@/src/shared/hooks/useAppRoute';
import {
    getGuidedTourSeen,
    updateGuidedTourSeen,
} from '@/src/shared/services/apiServices/usersApi';
import type { RootState } from '@/src/store';
import { userSlice } from '@/src/store/apps/user';

import CancelDialog from '../Modal/Cancel/CancelDialog';
import type { Device } from './AppTour.style';
import { allSteps, joyrideOptions, joyrideStyles } from './AppTour.style';

export default function AppTour() {
    const dispatch = useDispatch();
    const { t } = useTranslation('ginger');
    const { isPolli } = useAppRoute();
    const isMobile: boolean = useMediaQuery(mediumScreen);
    const [openGuidedTourDialog, setOpenGuidedTourDialog] = useState(false);

    const { runGuidedTour, tourCompleted, guidedTourIsSeen, userRole } = useSelector(
        (state: RootState) => ({
            runGuidedTour: state.user['state/runGuidedTour'],
            tourCompleted: state.user['state/tourCompleted'],
            guidedTourIsSeen: state.user['state/guidedTourIsSeen'],
            userRole: state.user['state/userRole']?.data?.role,
        })
    );

    useEffect(() => {
        if (guidedTourIsSeen || tourCompleted) return;

        const checkGuidedTour = async () => {
            try {
                const response = await getGuidedTourSeen();
                const isSeen = response.data?.isSeen === true;
                dispatch(userSlice.actions.guidedTourIsSeen(isSeen));

                if (isSeen) {
                    dispatch(userSlice.actions.tourCompleted(true));
                } else {
                    setOpenGuidedTourDialog(true);
                }
            } catch (error) {
                console.error('Error checking guided tour', error);
            }
        };

        checkGuidedTour();
    }, []);

    const handleJoyrideEvent = (data: EventData) => {
        const { action, status } = data;

        if (action === ACTIONS.CLOSE || status === STATUS.FINISHED) {
            dispatch(userSlice.actions.runGuidedTour(false));
            dispatch(userSlice.actions.tourCompleted(true));
        }
    };

    const markTourAsSeen = async () => {
        try {
            await updateGuidedTourSeen();
            dispatch(userSlice.actions.guidedTourIsSeen(true));
        } catch (error) {
            console.error('Error marking guided tour as seen', error);
        }

        setOpenGuidedTourDialog(false);
    };

    const handleGuidedTourStart = async () => {
        await markTourAsSeen();
        dispatch(userSlice.actions.runGuidedTour(true));
    };

    const handleGuidedTourClose = async () => {
        await markTourAsSeen();
        dispatch(userSlice.actions.tourCompleted(true));
    };

    const device: Device = isMobile ? 'mobile' : 'desktop';

    const guidedTourSteps = useMemo(() => {
        return allSteps
            .filter((step) => {
                // Polli tour should end at app drawer
                if (isPolli && step.id === 'release-notes') {
                    return false;
                }

                // role check
                if (step.roles && !step.roles.includes(userRole)) {
                    return false;
                }

                // device check
                if (step.devices && !step.devices.includes(device)) {
                    return false;
                }

                return true;
            })
            .map((step) => ({
                ...step,
                title: t(step.title),
                content: t(step.content),
            }));
    }, [device, userRole, isPolli, t]);

    return (
        <>
            <Joyride
                steps={guidedTourSteps}
                run={runGuidedTour}
                continuous
                onEvent={handleJoyrideEvent}
                options={joyrideOptions}
                styles={joyrideStyles}
                locale={{
                    back: t('guided_tour.button.previous'),
                    last: t('guided_tour.button.end'),
                    nextWithProgress: t('guided_tour.button.next'),
                }}
            />
            <CancelDialog
                open={openGuidedTourDialog}
                handleClose={handleGuidedTourClose}
                title={t('guided_tour.dialog.title')}
                dialogContent={t('guided_tour.dialog.content')}
                buttons={[
                    {
                        label: t('guided_tour.dialog.button.label'),
                        variant: 'contained',
                        handleClick: handleGuidedTourStart,
                    },
                ]}
                isExploreIconShow={true}
            />
        </>
    );
}
